import * as Sentry from '@sentry/browser';
import axios, { AxiosProgressEvent, AxiosRequestConfig, AxiosResponse } from 'axios';

import acquireAccessToken from './acquireAccessToken';

const { logger } = Sentry;

const makeGetRequestAsync = async <TReturn>(apiScopes: string[], url: string) => {
  const accessToken = await acquireAccessToken(apiScopes);
  const bearer = `Bearer ${accessToken}`;
  const config: AxiosRequestConfig = {
    headers: { Authorization: `${bearer}` }
  };
  try {
    return await axios.get<TReturn>(url, config);
  } catch (err) {
    logger.error('Error occurred in makeGetRequestAsync', {
      error: err,
      url: url
    });
    throw err; // Re-throw error so query can handle it
  }
};
export default makeGetRequestAsync;

export const makeGetBlobRequestAsync = async (apiScopes: string[], url: string) => {
  const accessToken = await acquireAccessToken(apiScopes);
  const bearer = `Bearer ${accessToken}`;
  const config: AxiosRequestConfig = {
    headers: { Authorization: `${bearer}` },
    responseType: 'blob'
  };
  try {
    return await axios.get<Blob>(url, config);
  } catch (err) {
    logger.error('Error occurred in makeGetBlobRequestAsync', {
      error: err,
      url: url
    });
    throw err; // Re-throw error so caller/query can handle it
  }
};

export const makePostRequestAsync = async <TReturn, TBody>(
  apiScopes: string[],
  url: string,
  data?: TBody
) => {
  const accessToken = await acquireAccessToken(apiScopes);
  const bearer = `Bearer ${accessToken}`;
  const config: AxiosRequestConfig = {
    headers: { Authorization: `${bearer}` }
  };

  if (!data) {
    return axios.post(url, null, config).catch((err) => {
      logger.error('Error occurred in makePostRequestAsync', {
        error: err,
        url: url
      });
      throw err; // CRITICAL: re-throw so callers see the failure (#7187/#7196 class, PBI #8080)
    }) as Promise<AxiosResponse<TReturn>>;
  }
  return axios.post(url, data, config).catch((err) => {
    logger.error('Error occurred in makePostRequestAsync', {
      error: err,
      url: url
    });
    throw err;
  }) as Promise<AxiosResponse<TReturn>>;
};

export const makePUTRequestAsync = async <TReturn, TBody>(
  apiScopes: string[],
  url: string,
  data: TBody
) => {
  const accessToken = await acquireAccessToken(apiScopes);
  const bearer = `Bearer ${accessToken}`;
  const config: AxiosRequestConfig = {
    headers: { Authorization: `${bearer}` }
  };
  return axios.put(url, data, config).catch((err) => {
    logger.error('Error occurred in makePUTRequestAsync', {
      error: err,
      url: url
    });
    throw err;
  }) as Promise<AxiosResponse<TReturn>>;
};

export const makePatchRequestAsync = async <TReturn, TBody>(
  apiScopes: string[],
  url: string,
  data: TBody
) => {
  const accessToken = await acquireAccessToken(apiScopes);
  const bearer = `Bearer ${accessToken}`;
  const config: AxiosRequestConfig = {
    headers: { Authorization: `${bearer}` }
  };
  return axios.patch(url, data, config).catch((err) => {
    logger.error('Error occurred in makePatchRequestAsync', {
      error: err,
      url: url
    });
    throw err; // CRITICAL: re-throw so callers see the failure (#7187/#7196)
  }) as Promise<AxiosResponse<TReturn>>;
};

export const makeDeleteRequestNoBodyAsync = async (apiScopes: string[], url: string) => {
  const accessToken = await acquireAccessToken(apiScopes);
  const bearer = `Bearer ${accessToken}`;
  const config: AxiosRequestConfig = {
    headers: { Authorization: `${bearer}` }
  };
  try {
    return await axios.delete(url, config);
  } catch (err) {
    logger.error('Error occurred in makeDeleteRequestNoBodyAsync', {
      error: err,
      url: url
    });
    throw err; // CRITICAL: Re-throw error so onError callback fires
  }
};

export const makePostFormRequestAsync = async <TReturn, TBody>(
  apiScopes: string[],
  url: string,
  data: TBody
) => {
  const accessToken = await acquireAccessToken(apiScopes);
  const bearer = `Bearer ${accessToken}`;
  const config: AxiosRequestConfig = {
    headers: {
      Authorization: `${bearer}`,
      'Content-Type': 'multipart/form-data'
    }
  };

  return axios.post(url, data, config).catch((err) => {
    logger.error('Error occurred in makePostFormRequestAsync', { error: err, url: url });
    const apiMessage = err?.response?.data;
    if (typeof apiMessage === 'string' && apiMessage) throw new Error(apiMessage);
    throw err;
  }) as Promise<AxiosResponse<TReturn>>;
};

export const makePatchFormRequestAsync = async <TReturn, TBody>(
  apiScopes: string[],
  url: string,
  data: TBody
) => {
  const accessToken = await acquireAccessToken(apiScopes);
  const bearer = `Bearer ${accessToken}`;
  const config: AxiosRequestConfig = {
    headers: {
      Authorization: `${bearer}`,

      'Content-Type': 'multipart/form-data'
    }
  };

  return axios.patch(url, data, config).catch((err) => {
    logger.error('Error occurred in makePatchFormRequestAsync', {
      error: err,
      url: url
    });
    throw err; // CRITICAL: re-throw so callers see the failure (#7187/#7196 class, PBI #8080)
  }) as Promise<AxiosResponse<TReturn>>;
};

export const downloadFileAsync = async (
  apiScopes: string[],
  url: string,
  fileName?: string,
  onDownloadProgressCallback?: (percetageDownloadProgress: number) => void
) => {
  const accessToken = await acquireAccessToken(apiScopes);
  const bearer = `Bearer ${accessToken}`;
  const config: AxiosRequestConfig = {
    url,
    headers: { Authorization: `${bearer}` },
    responseType: 'blob',
    method: 'GET',
    onDownloadProgress: (progressEvent: AxiosProgressEvent) =>
      onDownloadProgressCallback &&
      onDownloadProgressCallback(
        Math.round((progressEvent.loaded * 100) / (progressEvent.total ?? 1))
      )
  };
  return axios({ ...config })
    .then((response) => {
      const contentDisposition = response.headers['content-disposition'];
      let fName = fileName ?? 'downloaded-file.xlsx'; // default file name
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) {
          fName = match[1];
        }
      }

      const fileUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = fileUrl;
      link.setAttribute('download', fName); // or any other extension
      document.body.appendChild(link);
      link.click();
    })
    .catch((err) =>
      logger.error('Error occurred in downloadFileAsync', {
        error: err,
        url: url
      })
    );
};
